import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "./CreateCollection.scss";
import { getAccessToken } from "../../awsAuth";

const API = process.env.REACT_APP_API_BASE || "";

const CreateCollection = ({
  open,
  userId,
  onClose,
  onCreated,
  prefillName = "",
}) => {
  const [name, setName] = useState(prefillName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [isLeaving, setIsLeaving] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [kbOffset, setKbOffset] = useState(0);

  const inputRef = useRef(null);
  const leaveTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    // reset
    setName(prefillName || "");
    setError("");
    setBusy(false);
    setIsLeaving(false);

    // lock scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const t = setTimeout(() => inputRef.current?.focus(), 10);

    // keyboard lift (visualViewport)
    const vv = window.visualViewport;
    const handleVV = () => {
      if (!vv) return;
      const keyboardH = Math.max(0, window.innerHeight - vv.height);
      setKbOpen(keyboardH > 40);
      setKbOffset(keyboardH);
    };
    vv?.addEventListener?.("resize", handleVV);
    vv?.addEventListener?.("scroll", handleVV);
    handleVV();

    return () => {
      clearTimeout(t);
      vv?.removeEventListener?.("resize", handleVV);
      vv?.removeEventListener?.("scroll", handleVV);
      document.body.style.overflow = prevOverflow || "";
    };
  }, [open, prefillName]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && startLeave();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const startLeave = useCallback(() => {
    if (isLeaving || busy) return;
    setIsLeaving(true);
    clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => onClose?.(), 220);
  }, [isLeaving, busy, onClose]);

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) startLeave();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Назва колекції обовʼязкова");
      return;
    }
    try {
      setBusy(true);
      setError("");
      const token = await getAccessToken().catch(() => null);
      const res = await axios.post(`${API}/api/saved/collections`, {
        userId,
        name: trimmed,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated?.(res?.data || null);
      startLeave();
    } catch {
      setError("Не вдалося створити колекцію");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className={[
        "create-collection-page",
        isLeaving ? "is-leaving" : "",
        kbOpen ? "kb-open" : "",
      ].join(" ")}
      style={{ "--kb-offset": `${kbOffset}px` }}
      onClick={onBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-label="Створення нової колекції"
    >
      <div className="clc-shell" onClick={(e) => e.stopPropagation()}>
        <form className="clc-content" onSubmit={handleSubmit}>
          <div className="clc-field">
            <label className="clc-label" htmlFor="clc-input">
              Назва нової колекції
            </label>
            <input
              id="clc-input"
              ref={inputRef}
              type="text"
              className="clc-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              autoComplete="off"
              inputMode="text"
              enterKeyHint="done"
            />
          </div>

          {error ? <div className="clc-error">{error}</div> : null}

          <div className="clc-actions">
            <button
              type="button"
              className="clc-btn ghost"
              onClick={startLeave}
              disabled={busy}
            >
              Скасувати
            </button>
            <button
              type="submit"
              className="clc-btn primary"
              disabled={busy}
            >
              {busy ? "Створюємо…" : "Створити"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCollection;
